// Consumer portal application root.
//
// Routes (C.3 — Phase C):
//   /login            → LoginScreen (consumer-flavor chrome)
//   /coach            → CoachPage      (5th sidebar item, topmost)
//   /dashboard        → DashboardPage
//   /frameworks       → FrameworksPage
//   /notifications    → NotificationsPage
//   /adoption         → AdoptionPage   (tenant_admin only — RBAC by role, not URL)
//   /                 → redirect to /dashboard
//   *                 → unknown → redirect to /dashboard (V1; F surface designed in Wave 2)
//
// All authenticated routes mount inside <TenantShell> (212px dark sidebar
// + slim topbar + UserMenu). Per
// design/UI : UX v1.0/for-engineering-tenant-shell/specs/tenant-shell-strings.md.

import type { JSX } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { PortalProvider } from '@gc-coach/design-system';
import {
  AuthProvider,
  ProtectedRoute,
  SessionExpiredModal,
  SessionProvider,
} from './auth/index.js';
import { I18nProvider } from './i18n/index.js';
import { AuthRoutes } from './routes/auth/AuthRoutes.js';
import {
  AdoptionPage,
  CoachPage,
  DashboardPage,
  NotificationsPage,
} from './routes/index.js';
import {
  ActivationModal,
  FrameworkPreviewPage,
  FrameworkWorkspacePage,
  FrameworksLibraryPage,
} from './frameworks/index.js';
import { TenantShell } from './shell/index.js';

export function App(): JSX.Element {
  return (
    <BrowserRouter>
      <PortalProvider portal="consumer">
        <I18nProvider>
          <AuthProvider>
            <SessionProvider>
              <Routes>
                <Route path="/login" element={<AuthRoutes />} />

                <Route
                  path="/coach"
                  element={
                    <ProtectedRoute>
                      <TenantShell>
                        <CoachPage />
                      </TenantShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <TenantShell>
                        <DashboardPage />
                      </TenantShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/frameworks"
                  element={
                    <ProtectedRoute>
                      <TenantShell>
                        <FrameworksLibraryPage />
                      </TenantShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/frameworks/:frameworkId"
                  element={
                    <ProtectedRoute>
                      <TenantShell>
                        <FrameworkPreviewPage />
                      </TenantShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/frameworks/:frameworkId/activate"
                  element={
                    <ProtectedRoute>
                      <TenantShell>
                        {/* Modal is portal-rendered; the Preview page sits
                          * behind the scrim so the context stays visible. */}
                        <FrameworkPreviewPage />
                        <ActivationModal />
                      </TenantShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/frameworks/instances/:instanceId"
                  element={
                    <ProtectedRoute>
                      <TenantShell>
                        <FrameworkWorkspacePage />
                      </TenantShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/notifications"
                  element={
                    <ProtectedRoute>
                      <TenantShell>
                        <NotificationsPage />
                      </TenantShell>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/adoption"
                  element={
                    <ProtectedRoute>
                      <TenantShell>
                        <AdoptionPage />
                      </TenantShell>
                    </ProtectedRoute>
                  }
                />

                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
              <SessionExpiredModal />
            </SessionProvider>
          </AuthProvider>
        </I18nProvider>
      </PortalProvider>
    </BrowserRouter>
  );
}
